import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import type {
  BookedFlightItineraryWithDepartureTime,
  FlightItinerarySegment,
} from "@b2bportal/air-booking-api";
import {
  DisruptionRebookPlanResponseEnum,
  type DisruptionRefundPlanRequest,
  type RebookPlanSuccess,
  type TripSlice,
} from "@b2bportal/air-disruption-api";
import type { AppDispatch } from "@b2bportal/core-types";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  DisruptionExerciseProgress,
  resetDisruptionExerciseState,
  setDisruptionExerciseProgress,
} from "../../../../features/exercise/store/slice";
import { disruptionRebookPlan } from "../../../../features/exercise/thunks/disruptionRebookPlan";

export interface UseDisruptionRebookOnboardingParams {
  contractId?: string;
  disruptedFlight: BookedFlightItineraryWithDepartureTime;
  bookingReturn: boolean;
  onFlightSelectContinue: (props: {
    origin: string;
    destination: string;
    departure: string;
    tripSlice?: TripSlice;
  }) => void;
  setRebookPlanResponse: (response: RebookPlanSuccess) => void;
  setSelectedSlice: (slice: {
    segments: Array<FlightItinerarySegment>;
  }) => void;
}

export interface UseDisruptionRebookOnboardingReturn {
  loading: boolean;
  rebookPlanError: boolean;
  openBagsLearnMore: boolean;
  openReturnLearnMore: boolean;
  openMoreHelp: boolean;

  handleCta: () => void;
  handleGoBack: () => void;
  setRebookPlanError: (value: boolean) => void;
  onCloseBagsLearnMore: () => void;
  onCloseReturnLearnMore: () => void;
  onOpenReturnLearnMore: () => void;
  onOpenBagsLearnMore: () => void;
  onCloseMoreHelp: () => void;
  resetExerciseState: () => void;
}

export const useDisruptionRebookOnboarding = ({
  contractId,
  disruptedFlight,
  bookingReturn,
  onFlightSelectContinue,
  setRebookPlanResponse,
  setSelectedSlice,
}: UseDisruptionRebookOnboardingParams): UseDisruptionRebookOnboardingReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState<boolean>(false);
  const [rebookPlanError, setRebookPlanError] = useState<boolean>(false);
  const [openBagsLearnMore, setOpenBagsLearnMore] = useState(false);
  const [openReturnLearnMore, setOpenReturnLearnMore] = useState(false);
  const [openMoreHelp, setOpenMoreHelp] = useState(false);

  const handlePlanResponse = useCallback(
    (response: RebookPlanSuccess) => {
      setRebookPlanResponse(response);
      if (response.slices.length > 1) {
        dispatch(
          setDisruptionExerciseProgress(DisruptionExerciseProgress.FlightSelect)
        );
      } else if (response.slices.length === 1) {
        const flightSlice = response.slices[0];
        setSelectedSlice(flightSlice);
        if (flightSlice.segments.length === 1) {
          const origin = flightSlice.segments[0].origin.locationCode;
          const departure =
            flightSlice.segments[0].updatedDeparture ??
            flightSlice.segments[0].scheduledDeparture;
          const destination = flightSlice.segments[0].destination.locationCode;

          onFlightSelectContinue({
            origin,
            destination,
            departure,
            tripSlice: undefined,
          });

          dispatch(
            setDisruptionExerciseProgress(
              DisruptionExerciseProgress.FlightSearch
            )
          );
        } else if (flightSlice.segments.length > 1) {
          dispatch(
            setDisruptionExerciseProgress(
              DisruptionExerciseProgress.SegmentSelect
            )
          );
        } else {
          setOpenMoreHelp(true);
        }
      } else {
        setOpenMoreHelp(true);
      }
    },
    [dispatch, setRebookPlanResponse, setSelectedSlice, onFlightSelectContinue]
  );

  const fetchDisruptionRebookPlan = useCallback(() => {
    if (contractId) {
      setLoading(true);
      const req: DisruptionRefundPlanRequest = {
        contractId,
        itineraryId: disruptedFlight.bookedItinerary.id,
      };

      return dispatch(disruptionRebookPlan(req))
        .then((payload) => {
          const response = unwrapResult(payload);
          setLoading(false);
          if (
            response?.DisruptionRebookPlanResponse ===
            DisruptionRebookPlanResponseEnum.RebookPlanSuccess
          ) {
            handlePlanResponse(response);
          } else {
            setRebookPlanError(true);
            setLoading(false);
          }
        })
        .catch(() => {
          setRebookPlanError(true);
          setLoading(false);
        });
    }
  }, [contractId, dispatch, disruptedFlight, handlePlanResponse]);

  const handleCta = useCallback(() => {
    fetchDisruptionRebookPlan();
  }, [fetchDisruptionRebookPlan]);

  const handleGoBack = useCallback(() => {
    dispatch(
      setDisruptionExerciseProgress(DisruptionExerciseProgress.LandingPage)
    );
  }, [dispatch]);

  const resetExerciseState = useCallback(() => {
    dispatch(resetDisruptionExerciseState());
  }, [dispatch]);

  const onCloseBagsLearnMore = useCallback(
    () => setOpenBagsLearnMore(false),
    []
  );

  const onCloseReturnLearnMore = useCallback(
    () => setOpenReturnLearnMore(false),
    []
  );

  const onOpenReturnLearnMore = useCallback(
    () => setOpenReturnLearnMore(true),
    []
  );

  const onOpenBagsLearnMore = useCallback(() => setOpenBagsLearnMore(true), []);

  const onCloseMoreHelp = useCallback(() => setOpenMoreHelp(false), []);

  useEffect(() => {
    if (bookingReturn) {
      fetchDisruptionRebookPlan();
    }
  }, [fetchDisruptionRebookPlan, bookingReturn]);

  return {
    loading,
    rebookPlanError,
    openBagsLearnMore,
    openReturnLearnMore,
    openMoreHelp,
    handleCta,
    handleGoBack,
    setRebookPlanError,
    onCloseBagsLearnMore,
    onCloseReturnLearnMore,
    onOpenReturnLearnMore,
    onOpenBagsLearnMore,
    onCloseMoreHelp,
    resetExerciseState,
  };
};
